<template>
  <div class="p-4">
    <div class="flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
      <h2 class="m-0">Plantillas de mensaje</h2>
      <Button icon="pi pi-plus" label="Nueva" size="small" @click="openCreatePlantilla" />
    </div>

    <div class="card">
      <small class="text-500">
        Plantillas para armar el mensaje de WhatsApp a los grupos invitados.
      </small>

      <div v-if="!plantillas.length" class="text-500 text-sm mt-3">
        Todavía no hay plantillas.
      </div>
      <div class="flex flex-column gap-2 mt-3">
        <div
          v-for="p in plantillas"
          :key="p.id"
          class="flex align-items-center justify-content-between p-2 border-round"
          style="border: 1px solid var(--surface-border)"
        >
          <div class="overflow-hidden">
            <div class="font-bold">{{ p.nombre }}</div>
            <div class="text-xs text-500 white-space-nowrap overflow-hidden text-overflow-ellipsis">
              {{ p.texto }}
            </div>
          </div>
          <div class="flex gap-1">
            <Button icon="pi pi-pencil" size="small" severity="secondary" text @click="openEditPlantilla(p)" />
            <Button icon="pi pi-trash" size="small" severity="danger" text @click="deletePlantilla(p)" />
          </div>
        </div>
      </div>
    </div>

    <!-- Dialog: crear/editar una plantilla de mensaje -->
    <Dialog
      v-model:visible="showPlantillaEditor"
      :header="editingPlantilla ? 'Editar plantilla' : 'Nueva plantilla'"
      modal
      style="width: 520px"
    >
      <div class="flex flex-column gap-3 pt-2">
        <div class="flex flex-column gap-1">
          <label>Nombre</label>
          <InputText v-model="plantillaDraft.nombre" placeholder="Ej: Confirmación de grupo" autofocus />
        </div>
        <small class="text-500">
          Hacé clic en una variable para insertarla. Se reemplaza por el dato de cada grupo al enviar.
        </small>
        <div class="flex flex-wrap gap-1">
          <Tag
            v-for="v in MENSAJE_VARIABLES"
            :key="v.key"
            :value="v.label"
            severity="info"
            style="cursor: pointer"
            @click="insertVariable(v.key)"
          />
        </div>
        <Textarea v-model="plantillaDraft.texto" rows="8" autoResize placeholder="Escribí el mensaje..." />
        <div class="flex gap-2 justify-content-end">
          <Button label="Cancelar" severity="secondary" @click="showPlantillaEditor = false" />
          <Button label="Guardar" :loading="savingPlantillas" @click="savePlantillaEditor" />
        </div>
      </div>
    </Dialog>

    <ConfirmDialog />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import {
  useMensajePlantilla,
  MENSAJE_VARIABLES,
  DEFAULT_MENSAJE_PLANTILLA,
  nuevaPlantilla,
} from '@/composables/useMensajePlantilla'
import { useConfirm } from 'primevue/useconfirm'
import { useAppToast } from '@/composables/useAppToast'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Tag from 'primevue/tag'
import Textarea from 'primevue/textarea'
import ConfirmDialog from 'primevue/confirmdialog'

const confirm = useConfirm()
const toast = useAppToast()

const mensajeApi = useMensajePlantilla()
const plantillas = ref([])
const showPlantillaEditor = ref(false)
const editingPlantilla = ref(null)
const plantillaDraft = ref({ id: '', nombre: '', texto: '' })
const savingPlantillas = ref(false)

const loadPlantillas = async () => {
  const result = await mensajeApi.getPlantillas()
  plantillas.value = result.length ? result : [nuevaPlantilla('Predeterminada', DEFAULT_MENSAJE_PLANTILLA)]
}

const persistPlantillas = async (next) => {
  savingPlantillas.value = true
  try {
    const result = await mensajeApi.savePlantillas(next)
    if (result?.error) {
      toast.error('Ocurrió un error', result.error)
      return false
    }
    plantillas.value = next
    return true
  } finally {
    savingPlantillas.value = false
  }
}

const openCreatePlantilla = () => {
  editingPlantilla.value = null
  plantillaDraft.value = nuevaPlantilla()
  showPlantillaEditor.value = true
}

const openEditPlantilla = (p) => {
  editingPlantilla.value = p
  plantillaDraft.value = { ...p }
  showPlantillaEditor.value = true
}

const insertVariable = (key) => {
  plantillaDraft.value.texto += `{{${key}}}`
}

const savePlantillaEditor = async () => {
  if (!plantillaDraft.value.nombre?.trim()) return
  const next = editingPlantilla.value
    ? plantillas.value.map((p) => (p.id === editingPlantilla.value.id ? { ...plantillaDraft.value } : p))
    : [...plantillas.value, { ...plantillaDraft.value }]
  if (await persistPlantillas(next)) showPlantillaEditor.value = false
}

const deletePlantilla = (p) => {
  confirm.require({
    message: `¿Eliminar la plantilla "${p.nombre}"?`,
    header: 'Confirmar eliminación',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    acceptLabel: 'Eliminar',
    rejectLabel: 'Cancelar',
    accept: () => persistPlantillas(plantillas.value.filter((x) => x.id !== p.id)),
  })
}

onMounted(loadPlantillas)
</script>

<style scoped>
.card {
  background: var(--bg-primary);
  border-radius: var(--radius-lg);
  padding: var(--space-5);
  box-shadow: var(--shadow-1);
  text-align: left;
}
</style>
